import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@baseline/web/supabase";

export interface LibraryItem {
	id: string;
	added_at: string;
	source_type: "content" | "upload" | "document";
	item_type:
		| "youtube_video"
		| "ai_thread"
		| "website"
		| "url"
		| "document"
		| "upload";
	content_id: string | null;
	parent_id: string | null;
	title: string | null;
	doc_type: string | null;
	summary: string | null;
	contents: {
		id: string;
		title: string | null;
		canonical_url: string;
		og_image_url: string | null;
		domain: string | null;
		channel_name: string | null;
		status: string;
		error_message: string | null;
		summary: string | null;
	} | null;
	library_item_tags: { tags: { id: string; name: string } }[];
}

export function isContentProcessing(item: LibraryItem): boolean {
	return (
		item.source_type === "content" &&
		(item.contents?.status === "pending" ||
			item.contents?.status === "processing")
	);
}

export function useLibrary(userId: string | undefined) {
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: ["library", userId],
		queryFn: async () => {
			const { data, error } = await supabase
				.from("library_items")
				.select(
					`
					id,
					added_at,
					source_type,
					item_type,
					content_id,
					parent_id,
					title,
					doc_type,
					summary,
					contents (
						id,
						title,
						canonical_url,
						og_image_url,
						domain,
						channel_name,
						status,
						error_message,
						summary
					),
					library_item_tags (
						tags (
							id,
							name
						)
					)
				`,
				)
				.eq("user_id", userId!)
				.eq("is_archived", false)
				.order("added_at", { ascending: false });

			if (error) throw error;
			return (data as unknown as LibraryItem[]).sort((a, b) => {
				const aProcessing = isContentProcessing(a);
				const bProcessing = isContentProcessing(b);
				if (aProcessing && !bProcessing) return -1;
				if (!aProcessing && bProcessing) return 1;
				return (
					new Date(b.added_at).getTime() -
					new Date(a.added_at).getTime()
				);
			});
		},
		enabled: !!userId,
	});

	useEffect(() => {
		if (!userId) return;

		const channel = supabase
			.channel(`library:${userId}`)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "library_items",
					filter: `user_id=eq.${userId}`,
				},
				() => {
					queryClient.invalidateQueries({
						queryKey: ["library", userId],
					});
				},
			)
			.subscribe();

		return () => {
			supabase.removeChannel(channel);
		};
	}, [userId, queryClient]);

	const processingContentIds = useMemo(() => {
		if (!query.data) return [];
		return query.data
			.filter(isContentProcessing)
			.map((item) => item.content_id!)
			.sort();
	}, [query.data]);

	const processingKey = processingContentIds.join(",");

	useEffect(() => {
		if (!userId || processingContentIds.length === 0) return;

		let channel = supabase.channel(
			`library-contents:${userId}:${processingKey}`,
		);

		for (const contentId of processingContentIds) {
			channel = channel.on(
				"postgres_changes",
				{
					event: "UPDATE",
					schema: "public",
					table: "contents",
					filter: `id=eq.${contentId}`,
				},
				() => {
					queryClient.invalidateQueries({
						queryKey: ["library", userId],
					});
				},
			);
		}

		channel.subscribe();

		return () => {
			supabase.removeChannel(channel);
		};
	}, [userId, processingKey, queryClient]);

	return query;
}
